import React from 'react';

import { AddDiscountIcon } from '../rosettaSvgs';
import { Button, Input, Divider } from '@attentive-platform/stem-ui';
import { useRosettaStore } from '../../../Stores/useRosettaStore';
import { useShallow } from 'zustand/react/shallow';
import { calculateTradePriceForTradeHelper, calculatePricingHelper } from '../utils/rosettaPricingHelpers';

const PricingBreakdownCard = () => {
  const {
    tradesData,
    selectedTrades,
    volumeDiscountTiers,
    manualDiscount,
    manualDiscountEnabled,
    selectedScopes,
    inflationPercentage,
    perTradeDistribution,
    projectsPerYear,
    sheetsPerProject,
    dispatch
  } = useRosettaStore(
    useShallow(state => ({
      tradesData: state.tradesData,
      selectedTrades: state.selectedTrades,
      volumeDiscountTiers: state.volumeDiscountTiers,
      manualDiscount: state.manualDiscount,
      manualDiscountEnabled: state.manualDiscountEnabled,
      selectedScopes: state.selectedScopes,
      inflationPercentage: state.inflationPercentage,
      perTradeDistribution: state.perTradeDistribution,
      projectsPerYear: state.projectsPerYear,
      sheetsPerProject: state.sheetsPerProject,
      dispatch: state.dispatch
    }))
  );

  const totalAnnualSheets = projectsPerYear * sheetsPerProject;

  const calculateTradePriceForTrade = tradeId => {
    const trade = tradesData.find(t => t.trade_id === tradeId);
    const selectedScopeIds = Array.from(selectedScopes[tradeId] || []);
    return calculateTradePriceForTradeHelper(trade, selectedScopeIds, inflationPercentage);
  };

  const pricing = calculatePricingHelper({
    selectedTrades,
    tradesData,
    selectedScopes,
    inflationPercentage,
    perTradeDistribution,
    totalAnnualSheets,
    volumeDiscountTiers
  });

  const manualDiscountPercent = manualDiscountEnabled ? manualDiscount / 100 : 0;
  const finalPrice = pricing.priceAfterVolumeDiscount * (1 - manualDiscountPercent);
  const annualCost = pricing.annualCostAfterVolumeDiscount * (1 - manualDiscountPercent);

  const handleManualDiscountToggle = () => {
    dispatch({ type: 'SET_MANUAL_DISCOUNT_ENABLED', payload: !manualDiscountEnabled });
  };

  const handleManualDiscountChange = e => {
    const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
    dispatch({ type: 'SET_MANUAL_DISCOUNT', payload: value });
  };

  return (
    <section className='rosetta-pricing-card'>
      <div className='rosetta-pricing-header'>
        <h2 className='rosetta-pricing-title'>Pricing Breakdown</h2>
        <Button variant='outlined' onClick={handleManualDiscountToggle} size='small'>
          <AddDiscountIcon />
        </Button>
      </div>

      {/* Price per Trade */}
      <div className='rosetta-price-per-trade-section'>
        <h3 className='rosetta-price-per-trade-title'>Price per Trade</h3>
        <div className='rosetta-trade-prices-list'>
          {Array.from(selectedTrades).map(tradeId => {
            const trade = tradesData.find(t => t.trade_id === tradeId);
            const tradePrice = calculateTradePriceForTrade(tradeId);
            return (
              <div key={tradeId} className='rosetta-trade-price-item'>
                <span className='rosetta-trade-price-name'>{trade.trade_name}:</span>
                <span className='rosetta-trade-price-value'>${tradePrice.toFixed(1)}/sheet</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className='rosetta-pricing-row'>
        <span>Blended List Price:</span>
        <span>${pricing.blendedPrice.toFixed(1)}/sheet</span>
      </div>
      <div className='rosetta-pricing-row discount'>
        <span>Volume Discount:</span>
        <span>-{(pricing.volumeDiscount * 100).toFixed(0)}%</span>
      </div>
      {manualDiscountEnabled && (
        <div className='rosetta-pricing-row discount'>
          <span>Manual Discount:</span>
          <span>-{manualDiscount}%</span>
        </div>
      )}
      <div className='rosetta-pricing-row final'>
        <span>Final Price:</span>
        <span>${finalPrice.toFixed(1)}/sheet</span>
      </div>
      <div className='rosetta-pricing-row total'>
        <span>Estimated Annual Cost:</span>
        <span>${annualCost.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
      </div>

      <Divider />

      {manualDiscountEnabled && (
        <div className='rosetta-form-group'>
          <label className='rosetta-form-label'>Manual Discount (%)</label>
          <Input
            type='number'
            value={manualDiscount === 0 ? '' : manualDiscount}
            onChange={handleManualDiscountChange}
            min='0'
            max='100'
            step='0.5'
            placeholder='0'
            size='small'
          />
        </div>
      )}
    </section>
  );
};

export default PricingBreakdownCard;
